import React from "react";

/**
 * Recharts custom tick that solves the "long localized X-axis labels
 * collide with their neighbours" problem on profile/category charts.
 *
 * Strategy (uniform across EN / ES / ZH):
 *  - Render the FULL label on a single line — no wrapping, no splitting.
 *  - Rotate it so every label gets its own diagonal slot, so adjacent
 *    labels never touch even when the longest one is much longer than
 *    the bar's width.
 *  - Latin (EN / ES) uses a steeper -32° angle to compress wider Latin
 *    glyph runs into a manageable bottom strip; CJK (ZH/JA/KO) uses a
 *    softer -20° because its glyphs are square and short labels read
 *    better at a shallower tilt.
 *  - Font is slightly smaller for CJK (9px) than Latin (10px).
 *
 * The component is data-agnostic — it only formats whatever string the
 * chart passes in. Internal analytics keys stay untouched on the chart
 * datapoint.
 */
export const StaggeredCategoryTick = (props) => {
  const {
    x, y, payload, lang,
    fontSize, latinRotation = -32, cjkRotation = -20,
  } = props || {};
  const label = String(payload?.value ?? "");

  // Heuristic: treat the label as CJK if it contains any Han / Kana /
  // Hangul codepoint OR the active UI language is one of zh/ja/ko. The
  // language hint is the primary signal so an English-only seed string
  // displayed in a ZH UI still picks the Latin layout.
  const looksCJK = /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uAC00-\uD7AF]/.test(label);
  const useCJK = lang === "zh" || lang === "ja" || lang === "ko" || looksCJK;

  const fz = fontSize ?? (useCJK ? 9 : 10);
  const rot = useCJK ? cjkRotation : latinRotation;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="end"
        fill="#475569"
        fontSize={fz}
        dy={6}
        transform={`rotate(${rot})`}
      >
        {label}
      </text>
    </g>
  );
};

export default StaggeredCategoryTick;
